import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { ModalComponent } from 'projects/component-library/components/modal/modal.component';

import { ExpectedError, HttpErrorResponse } from 'projects/shared/services/models/error.response';
import { DeleteUsersRequest } from 'projects/shared/services/inspection-companies/inspection-company.user.service';
import { UserWithRolesResponse } from 'projects/shared/services/inspection-companies/models/users/user-with-roles.response';
import { UsersDeletedResponse } from 'projects/shared/services/inspection-companies/models/users/users-deleted-response';
import { Observable } from 'rxjs';

@Component({
  selector: 'shared-delete-users-bulk-modal',
  standalone: false,
  styles: [
    `
      .modelBody {
        display: flex;
        flex-direction: column;
        padding: 0;
        gap: 0;
      }

      .warning-text {
        padding: 24px 0;
      }
    `,
  ],
  template: `
    <mac-modal [(open)]="isVisible" #modal (close)="closeModal()">
      <mac-modal-title>
        {{ 'Portal.InspectionCompanies.Users.BulkDelete.Title' | translate }}
      </mac-modal-title>

      <mac-modal-content>
        <app-alert type="error" [errorKeys]="errors"></app-alert>
        <div class="warning-text">
          <p style="margin-bottom: 5px">{{ 'Portal.InspectionCompanies.Users.BulkDelete.Confirmation' | translate }}</p>
          <p style="margin-bottom: 0">{{ 'Portal.InspectionCompanies.Users.BulkDelete.Warning' | translate }}</p>
        </div>
        <div class="modelBody" nz-row>
          <div *ngFor="let user of users; let i = index">
            <shared-delete-bulk-user-item
              [user]="user"
              [isLoading]="isLoading"
              [itemIndex]="i"
              [response]="response"
              (userRemoved)="removeUser($event)" />
          </div>
        </div>
      </mac-modal-content>

      <mac-modal-footer>
        <ng-container *ngIf="response">
          <mac-button (click)="closeModal()">
            {{ 'Shared.Modal.OkButton.Label' | translate }}
          </mac-button>
        </ng-container>
        <ng-container *ngIf="!response">
          <mac-modal-cancel-button (cancel)="closeModal()" [isDisabled]="isLoading"> </mac-modal-cancel-button>
          <mac-button (click)="onDeleteUsers()" [isLoading]="isLoading">
            {{ 'Portal.InspectionCompanies.Users.All.BulkActions.Delete.Label' | translate }}
          </mac-button>
        </ng-container>
      </mac-modal-footer>
    </mac-modal>
  `,
})
export class DeleteUsersBulkModalComponent {
  @Input() isVisible? = false;
  @Input() usersToDelete: UserWithRolesResponse[] = [];
  @Input({ required: true }) deleteUsersAction: (model: DeleteUsersRequest) => Observable<UsersDeletedResponse>;

  @Output() isVisibleChange = new EventEmitter<boolean>();
  @Output() usersDeleted = new EventEmitter<boolean>();

  @ViewChild('modal') modal: ModalComponent;

  response: UsersDeletedResponse | undefined;
  errors: ExpectedError[] = [];

  users: UserWithRolesResponse[] = [];
  isLoading: boolean;

  openModal(): void {
    this.users = this.usersToDelete;
    this.modal.openModal();
  }

  closeModal(): void {
    this.errors = [];
    this.response = undefined;
    this.modal.closeModal();
    this.usersDeleted.emit(false);
  }

  removeUser(index: number): void {
    if (this.users.length === 1) {
      this.closeModal();
      return;
    }
    this.users.splice(index, 1);
  }

  onDeleteUsers() {
    this.isLoading = true;
    const request = {
      userEmails: this.users.map((user) => user.email),
    };
    this.deleteUsersAction(request).subscribe({
      next: (response) => {
        this.usersDeleted.emit(true);
        this.response = response;
        this.mapErrors(response);
      },
      error: (error: HttpErrorResponse) => {
        this.errors = error.error.errors;
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  private mapErrors(response: UsersDeletedResponse) {
    const deleteUserErrorResponses = response.deleteUserResponses.filter((x) => !x.isDeleted).map((result) => result.error);
    this.errors = Array.from(new Set(deleteUserErrorResponses.flatMap((error) => error.key)));
  }
}

@Component({
  selector: 'shared-delete-bulk-user-item',
  standalone: false,
  styles: [
    `
      @use '../../../component-library/styles/colors.scss' as colors;

      .userRow {
        display: flex;
        height: 48px;
        padding: 12px 24px;
        flex-shrink: 0;
        border-radius: 4px;
        border: 1px solid colors.$neutral-6;
        background: colors.$neutral-2;
        margin-bottom: 16px;
        font-weight: 600;
      }

      .success {
        border: 1px solid colors.$brand-5;
        background: colors.$alert-success-0;
      }

      .error {
        border: 1px solid colors.$alert-error-5;
        background: colors.$alert-error-0;
        color: colors.$alert-error-5;
      }

      .successText {
        color: colors.$brand-5;
      }

      .errorText {
        color: colors.$alert-error-5;
      }

      .loader {
        color: colors.$brand-5;
        font-size: 20px;
      }
    `,
  ],
  template: `
    <div class="userRow" [class.success]="isUserDeleted() === true" [class.error]="isUserDeleted() === false">
      <div nz-col nzFlex="auto">
        <ng-container *ngIf="!user.firstName && !user.lastName">{{ user.email }}</ng-container>
        {{ user.firstName }} {{ user.lastName }}
      </div>
      <ng-container *ngIf="!response">
        <i nz-col *ngIf="isLoading" nz-icon nzType="loading" nzTheme="outline" class="loader"></i>
        <mac-icon *ngIf="!isLoading" nz-col (click)="onRemoveUser()" style="cursor: pointer" [icon]="'close'"> </mac-icon>
      </ng-container>
      <ng-container *ngIf="response">
        <div nz-col [class]="isUserDeleted() ? 'successText' : 'errorText'">
          <mac-icon nz-col [icon]="isUserDeleted() ? 'check' : 'close'"></mac-icon>
        </div>
      </ng-container>
    </div>
  `,
})
export class DeleteUsersBulkRowComponent {
  @Input() user: UserWithRolesResponse;
  @Input() isLoading: boolean;
  @Input() itemIndex: number;
  @Input() response: UsersDeletedResponse | undefined;

  @Output() userRemoved = new EventEmitter<number>();

  isUserDeleted(): boolean | undefined {
    return this.response?.deleteUserResponses.find((result) => result.email === this.user.email)?.isDeleted;
  }

  public onRemoveUser() {
    this.userRemoved.emit(this.itemIndex);
  }
}
